import { instance } from "../axios"
import type { AxiosRequestConfig } from "axios"
import type {
    TagAssignerComparisonResponse,
    TagAssignerCopyResponse,
    TagAssignerItem,
    TagAssignerListResponse,
    TagAssignerMemberListResponse,
    TagAssignerStatusListResponse,
    TagAssignerSummaryResponse,
    TagAssignerTypeListResponse,
    TagComparisonRequestResponse,
    TagComparisonResultResponse,
    TagItem,
    TagListResponse,
    TagMemberListResponse,
    TagStatusListResponse,
    TagSummaryListResponse,
} from "./types"

// 取得可用的標籤列表
export function getTagList(config: AxiosRequestConfig = {}) {
    return instance.request<TagListResponse>({
        url: "/tags/",
        method: "get",
        ...config,
    })
}

// 取得標籤狀態
export function getTagStatus(config: AxiosRequestConfig = {}) {
    return instance.request<TagStatusListResponse>({
        url: "/tags/status/",
        method: "get",
        ...config,
    })
}

// 新增一個標籤
export function createdTag(config: AxiosRequestConfig = {}) {
    return instance.request<TagItem>({
        url: "/tags/",
        method: "post",
        ...config,
    })
}

// 取得篩選貼標列表
export function getTagAssignerList(config: AxiosRequestConfig = {}) {
    return instance.request<TagAssignerListResponse>({
        url: "/tags/assigners/",
        method: "get",
        ...config,
    })
}

// 新增篩選貼標資料
export function addTagAssigner(config: AxiosRequestConfig = {}) {
    return instance.request<TagAssignerItem>({
        url: "/tags/assigners/",
        method: "post",
        ...config,
    })
}

// 複製篩選貼標資料
export function copyTagAssigner(
    taggingUuid: string,
    config: AxiosRequestConfig = {}
) {
    return instance.request<TagAssignerCopyResponse>({
        url: `/tags/assigners/${taggingUuid}/copy/`,
        method: "post",
        ...config,
    })
}

// 取得單一篩選貼標資料
export function getTagAssigner(
    taggingUuid: string,
    config: AxiosRequestConfig = {}
) {
    return instance.request<TagAssignerItem>({
        url: `/tags/assigners/${taggingUuid}/`,
        method: "get",
        ...config,
    })
}

// 更新單一篩選貼標資料
export function updateTagAssigner(
    taggingUuid: string,
    config: AxiosRequestConfig = {}
) {
    return instance.request<TagAssignerItem>({
        url: `/tags/assigners/${taggingUuid}/`,
        method: "put",
        ...config,
    })
}

// 局部更新單一篩選貼標資料
export function patchTagAssigner(
    taggingUuid: string,
    config: AxiosRequestConfig = {}
) {
    return instance.request<TagAssignerItem>({
        url: `/tags/assigners/${taggingUuid}/`,
        method: "patch",
        ...config,
    })
}

// 停用單一篩選貼標資料
export function disableTagAssigner(
    taggingUuid: string,
    config: AxiosRequestConfig = {}
) {
    return instance.request<void>({
        url: `/tags/assigners/${taggingUuid}/disable/`,
        method: "post",
        ...config,
    })
}

// 刪除單一篩選貼標資料
export function deleteTagAssigner(
    taggingUuid: string,
    config: AxiosRequestConfig = {}
) {
    return instance.request<void>({
        url: `/tags/assigners/${taggingUuid}/`,
        method: "delete",
        ...config,
    })
}

// 新增篩選貼標
export function postTagAssignerAssign(
    taggingUuid: string,
    config: AxiosRequestConfig = {}
) {
    return instance.request<void>({
        url: `/tags/assigners/${taggingUuid}/assign/`,
        method: "post",
        ...config,
    })
}

// 比較篩選貼標
export function compareTagAssigners(config: AxiosRequestConfig = {}) {
    return instance.request<TagAssignerComparisonResponse>({
        url: "/tags/assigners/compare/",
        method: "post",
        ...config,
    })
}

// 取得篩選貼標名單
export function getTagAssignerMembers(
    taggingUuid: string,
    config: AxiosRequestConfig = {}
) {
    return instance.request<TagAssignerMemberListResponse>({
        url: `/tags/assigners/${taggingUuid}/members/`,
        method: "get",
        ...config,
    })
}

// 取得篩選貼標分析資料
export function getTagAssignerSummary(
    taggingUuid: string,
    config: AxiosRequestConfig = {}
) {
    return instance.request<TagAssignerSummaryResponse>({
        url: `/tags/assigners/${taggingUuid}/summary/`,
        method: "get",
        ...config,
    })
}

// 取得篩選貼標狀態
export function getTagAssignerStatus(config: AxiosRequestConfig = {}) {
    return instance.request<TagAssignerStatusListResponse>({
        url: `/tags/assigners/status/`,
        method: "get",
        ...config,
    })
}

// 取得篩選貼標類型
export function getTagAssignerType(config: AxiosRequestConfig = {}) {
    return instance.request<TagAssignerTypeListResponse>({
        url: `/tags/assigners/type/`,
        method: "get",
        ...config,
    })
}

// 下載貼標名單
export function downloadTagAssignerMemberList(taggingUuid: string, config: AxiosRequestConfig = {}) {
    return instance.request<void>({
        url: `/tags/assigners/${taggingUuid}/export/`,
        method: "post",
        ...config,
    })
}

// 比較多筆標籤
export function compareTags(config: AxiosRequestConfig = {}) {
    return instance.request<TagComparisonRequestResponse>({
        url: "/tags/compare/",
        method: "post",
        ...config,
    })
}

// 取得標籤比較結果
export function getTagsComparison(tagComparisonUuid: string, config: AxiosRequestConfig = {}) {
    return instance.request<TagComparisonResultResponse>({
        url: `/tags/comparison/${tagComparisonUuid}/`,
        method: "get",
        ...config,
    })
}

// 取得標籤資訊
export function getTagInfo(tagUuid: string, config: AxiosRequestConfig = {}) {
    return instance.request<TagItem>({
        url: `/tags/${tagUuid}/`,
        method: "get",
        ...config,
    })
}

// 編輯標籤名稱
export function editTag(tagUuid: string, config: AxiosRequestConfig = {}) {
    return instance.request<TagItem>({
        url: `/tags/${tagUuid}/`,
        method: "put",
        ...config,
    })
}

// 刪除標籤
export function deleteTag(tagUuid: string, config: AxiosRequestConfig = {}) {
    return instance.request<void>({
        url: `/tags/${tagUuid}/`,
        method: "delete",
        ...config,
    })
}

// 停用標籤
export function disableTag(tagUuid: string, config: AxiosRequestConfig = {}) {
    return instance.request<void>({
        url: `/tags/${tagUuid}/disable/`,
        method: "post",
        ...config,
    })
}

// 取得標籤會員列表
export function getTagMemberList(
    tagUuid: string,
    config: AxiosRequestConfig = {}) {
    return instance.request<TagMemberListResponse>({
        url: `/tags/${tagUuid}/members/`,
        method: "get",
        ...config,
    })
}

// 取得標籤分析資料
export function getTagSummary(
    tagUuid: string,
    config: AxiosRequestConfig = {}) {
    return instance.request<TagSummaryListResponse>({
        url: `/tags/${tagUuid}/summary/`,
        method: "get",
        ...config,
    })
}

// 匯出標籤會員名單
export function exportTagNameList(
    tagUuid: string,
    config: AxiosRequestConfig = {}) {
    return instance.request<void>({
        url: `/tags/${tagUuid}/export/`,
        method: "post",
        ...config,
    })
}
